import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import {
  deleteOvrRecords,
  getDataSummary,
  getManageableOvrRecords,
  hideOvrRecords,
  unhideOvrRecords,
} from '../services/api';
import { setAlert } from '../store/slices/alertSlice';
import ConfirmActionModal from './ConfirmActionModal';

const PAGE_SIZE = 15;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const ArchiveOvr = () => {
  const dispatch = useDispatch();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  const loadSummary = useCallback(async () => {
    const { data } = await getDataSummary();
    setSummary(data);
  }, []);

  const loadRows = useCallback(async (nextPage, nextSearch, nextVisibility) => {
    const { data } = await getManageableOvrRecords({
      page: nextPage,
      limit: PAGE_SIZE,
      search: nextSearch,
      visibility: nextVisibility,
    });
    setRows(data.incidents || []);
    setTotal(data.total || 0);
    setPage(data.page || nextPage);
  }, []);

  const refresh = useCallback(async (nextPage = page, nextSearch = appliedSearch, nextVisibility = visibility) => {
    setLoading(true);
    try {
      await Promise.all([
        loadSummary(),
        loadRows(nextPage, nextSearch, nextVisibility),
      ]);
      setSelected([]);
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Could not load OVR records',
      }));
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, dispatch, loadRows, loadSummary, page, visibility]);

  useEffect(() => {
    refresh(1, '', 'all');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedRows = rows.filter((row) => selected.includes(row._id));
  const selectedVisible = selectedRows.filter((row) => !row.isHidden).length;
  const selectedHidden = selectedRows.filter((row) => row.isHidden).length;
  const busy = Boolean(busyAction);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const changeVisibility = async (nextVisibility) => {
    setVisibility(nextVisibility);
    setSelected([]);
    await refresh(1, appliedSearch, nextVisibility);
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    setAppliedSearch(search);
    await refresh(1, search, visibility);
  };

  const toggleSelected = (id) => {
    setSelected((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const togglePage = () => {
    if (selected.length === rows.length) {
      setSelected([]);
      return;
    }
    setSelected(rows.map((row) => row._id));
  };

  const runPendingAction = async () => {
    if (!pendingAction) return;
    const action = pendingAction;
    setBusyAction(action.type);
    try {
      let response;
      if (action.type === 'hide-selected') {
        response = await hideOvrRecords({ ids: selected });
      } else if (action.type === 'hide-all') {
        response = await hideOvrRecords({ all: true });
      } else if (action.type === 'restore-selected') {
        response = await unhideOvrRecords({ ids: selected });
      } else if (action.type === 'restore-all') {
        response = await unhideOvrRecords({ all: true });
      } else if (action.type === 'delete-selected') {
        response = await deleteOvrRecords({ scope: 'selected', ids: selected });
      } else if (action.type === 'delete-archived') {
        response = await deleteOvrRecords({ scope: 'archived' });
      } else if (action.type === 'delete-all') {
        response = await deleteOvrRecords({ scope: 'all' });
      }
      dispatch(setAlert({
        type: 'success',
        message: response?.data?.message || 'Done',
      }));
      setPendingAction(null);
      await refresh(page, appliedSearch, visibility);
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Action failed',
      }));
    } finally {
      setBusyAction('');
    }
  };

  const ask = (action) => setPendingAction(action);

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h3>Hide / Archive OVR</h3>
          <p>
            Select records, then hide, restore, or delete them. Hidden records stay stored but are
            left out of the dashboard, reports, and counts. Deleting does not change the next OVR number
            {summary?.refNo?.nextRefNo ? ` (${summary.refNo.nextRefNo})` : ''}.
          </p>
        </div>
      </div>

      <div className="data-summary card p-3 shadow-sm">
        <span><strong>{summary?.visibleIncidents ?? 0}</strong> visible</span>
        <span><strong>{summary?.hiddenIncidents || 0}</strong> archived</span>
        <span><strong>{summary?.incidents || 0}</strong> total</span>
        <span><strong>{summary?.refNo?.nextRefNo || '—'}</strong> next number</span>
      </div>

      <section className="card p-3 shadow-sm data-tool-card">
        <div className="ovr-toolbar">
          <div className="ovr-filter-tabs" role="tablist">
            {[
              ['all', 'All'],
              ['visible', 'Visible'],
              ['hidden', 'Archived'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={visibility === value ? 'active' : ''}
                onClick={() => changeVisibility(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <Form onSubmit={handleSearch} className="ovr-search">
            <Form.Control
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search ref no, name, or status"
            />
            <Button type="submit" variant="secondary">Search</Button>
          </Form>
        </div>

        <div className="ovr-actions">
          <Button
            type="button"
            variant="warning"
            disabled={busy || selectedVisible < 1}
            onClick={() => ask({
              type: 'hide-selected',
              title: 'Hide selected OVR?',
              message: `Hide ${selectedVisible} selected visible record(s)? They will leave the dashboard and reports, and stay in this archive list.`,
              confirmLabel: 'Hide selected',
              variant: 'warning',
            })}
          >
            Hide selected ({selectedVisible})
          </Button>
          <Button
            type="button"
            variant="outline-primary"
            disabled={busy || selectedHidden < 1}
            onClick={() => ask({
              type: 'restore-selected',
              title: 'Restore selected OVR?',
              message: `Show ${selectedHidden} selected archived record(s) on the dashboard and reports again?`,
              confirmLabel: 'Restore selected',
              variant: 'primary',
            })}
          >
            Restore selected ({selectedHidden})
          </Button>
          <Button
            type="button"
            variant="outline-danger"
            disabled={busy || !selected.length}
            onClick={() => ask({
              type: 'delete-selected',
              title: 'Delete selected OVR?',
              message: `Permanently delete ${selected.length} selected record(s)? This cannot be undone. The next OVR number will stay ${summary?.refNo?.nextRefNo || 'the same'}.`,
              confirmLabel: 'Delete selected',
              variant: 'danger',
            })}
          >
            Delete selected ({selected.length})
          </Button>
          <Button
            type="button"
            variant="outline-warning"
            disabled={busy || !(summary?.visibleIncidents > 0)}
            onClick={() => ask({
              type: 'hide-all',
              title: 'Hide all visible OVR?',
              message: `Archive all ${summary?.visibleIncidents || 0} visible records? They will disappear from every report and dashboard.`,
              confirmLabel: 'Hide all visible',
              variant: 'warning',
            })}
          >
            Hide all visible
          </Button>
          <Button
            type="button"
            variant="outline-secondary"
            disabled={busy || !(summary?.hiddenIncidents > 0)}
            onClick={() => ask({
              type: 'restore-all',
              title: 'Restore all archived OVR?',
              message: `Make all ${summary?.hiddenIncidents || 0} archived records visible again?`,
              confirmLabel: 'Restore all',
              variant: 'primary',
            })}
          >
            Restore all archived
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={busy || !(summary?.incidents > 0)}
            onClick={() => ask({
              type: 'delete-all',
              title: 'Delete all OVR records?',
              message: `Permanently delete all ${summary?.incidents || 0} OVR records? Users, roles, and settings stay. The next number will not go backwards.`,
              confirmLabel: 'Delete all OVR',
              variant: 'danger',
            })}
          >
            Delete all OVR
          </Button>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>
                    <Form.Check
                      type="checkbox"
                      checked={rows.length > 0 && selected.length === rows.length}
                      onChange={togglePage}
                      aria-label="Select all OVR on this page"
                    />
                  </th>
                  <th>Ref No</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Department</th>
                  <th>Date</th>
                  <th>Visibility</th>
                </tr>
              </thead>
              <tbody>
                {!rows.length && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No OVR records in this view.
                    </td>
                  </tr>
                )}
                {rows.map((item) => (
                  <tr key={item._id} className={selected.includes(item._id) ? 'table-active' : ''}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selected.includes(item._id)}
                        onChange={() => toggleSelected(item._id)}
                        aria-label={`Select ${item.refNo}`}
                      />
                    </td>
                    <td className="admin-strong-cell">{item.refNo}</td>
                    <td>{item.name}</td>
                    <td>{item.currentStatus}</td>
                    <td>{item.department?.departmentName || '—'}</td>
                    <td>{formatDate(item.date || item.submittedDate)}</td>
                    <td>
                      <span className={`ovr-visibility ${item.isHidden ? 'archived' : 'visible'}`}>
                        {item.isHidden ? 'Archived' : 'Visible'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted">
            {total === 0 ? '0 records' : `Showing ${((page - 1) * PAGE_SIZE) + 1}-${Math.min(page * PAGE_SIZE, total)} of ${total}`}
          </span>
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page <= 1 || loading}
              onClick={() => refresh(page - 1, appliedSearch, visibility)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page >= totalPages || loading}
              onClick={() => refresh(page + 1, appliedSearch, visibility)}
            >
              Next
            </Button>
          </div>
        </div>
      </section>

      <ConfirmActionModal
        show={Boolean(pendingAction)}
        title={pendingAction?.title}
        message={pendingAction?.message}
        confirmLabel={pendingAction?.confirmLabel}
        variant={pendingAction?.variant || 'primary'}
        busy={busy}
        onCancel={() => !busy && setPendingAction(null)}
        onConfirm={runPendingAction}
      />
    </div>
  );
};

export default ArchiveOvr;
